import React, { FC, MouseEvent, KeyboardEvent } from 'react';
import { IconProps, Icon } from '../components/Icon';
import { Box, Button, ButtonGroup, CloseButton, HStack, useMultiStyleConfig, UseToastOptions } from '@chakra-ui/react';
import { Typography } from '../components/Typography';

type AlertVariants = 'success' | 'error' | 'info' | 'warning' | 'neutral';

export type Props = Pick<UseToastOptions, 'isClosable'> & {
  variant: AlertVariants;
  title: string;
  description: string;
  actions?: {
    primary?: {
      label: string;
      onClick: (e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
    };
    secondary?: {
      label: string;
      onClick: (e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
    };
  };
  onClose?: (e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
  showIcon?: boolean;
};

export const Alert: FC<Props> = ({ variant, title, description, actions, isClosable, onClose, showIcon }) => {
  const variantToIcon: Record<AlertVariants, IconProps> = {
    success: {
      prefix: 'fas',
      iconName: 'check-circle',
    },
    error: {
      prefix: 'fas',
      iconName: 'octagon-exclamation',
    },
    info: {
      prefix: 'fas',
      iconName: 'info-circle',
    },
    warning: {
      prefix: 'fas',
      iconName: 'exclamation-triangle',
    },
    neutral: {
      prefix: 'far',
      iconName: 'bell',
    },
  };

  /**
   * Hooks
   */
  const styles = useMultiStyleConfig('Alert', { variant });

  /**
   * DOM
   */
  return (
    <HStack data-object-type="alert" align="flex-start" __css={styles.container} display="inline-flex">
      {showIcon && (
        <Box __css={styles.icon} mr="2" mt="1">
          <Icon {...variantToIcon[variant]} size={'lg'} />
        </Box>
      )}
      <Box flexGrow={1}>
        <Box __css={styles.title}>
          <Typography variant={'h4'}>{title}</Typography>
        </Box>
        <Box __css={styles.description}>
          <Typography textStyle={'p2'}>{description}</Typography>
        </Box>
        {(actions?.primary || actions?.secondary) && (
          <ButtonGroup spacing={'2'} mt={'3'} colorScheme={'gray'}>
            {actions.primary && (
              <Button variant={'solid'} onClick={actions.primary.onClick}>
                {actions.primary.label}
              </Button>
            )}
            {actions.secondary && (
              <Button variant={'outline'} onClick={actions.secondary.onClick}>
                {actions.secondary.label}
              </Button>
            )}
          </ButtonGroup>
        )}
      </Box>
      {isClosable && <CloseButton size={'sm'} onClick={onClose} />}
    </HStack>
  );
};
